"use client"

import * as React from "react"
import { format, subDays, previousFriday } from "date-fns"
import { Calendar as CalendarIcon } from "lucide-react"
import { DateRange } from "react-day-picker"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"

interface DateRangePickerProps {
  dateRange: DateRange | undefined
  onDateRangeChange: (range: DateRange | undefined) => void
  className?: string
}

type Preset = {
  label: string
  days?: number
  getValue?: () => { from: Date; to: Date }
}

const presets: Preset[] = [
  { 
    label: "Friday -> Monday (now)", 
    getValue: () => {
      const now = new Date();
      return {
        from: previousFriday(now),
        to: now
      };
    }
  },
  { label: "24 hours", days: 1 },
  { label: "7 days", days: 7 },
  { label: "2 weeks", days: 14 },
  { label: "4 weeks", days: 28 },
  { label: "2 months", days: 60 },
  { label: "3 months", days: 90 },
  { label: "6 months", days: 180 },
]

export function DateRangePicker({
  dateRange,
  onDateRangeChange,
  className,
}: DateRangePickerProps) {
  const [open, setOpen] = React.useState(false)

  const handlePresetClick = (preset: Preset) => {
    if (preset.getValue) {
      onDateRangeChange(preset.getValue())
    } else if (preset.days) {
      const end = new Date()
      const start = subDays(end, preset.days)
      onDateRangeChange({ from: start, to: end })
    }
  }

  const getDaysSelected = () => {
    if (!dateRange?.from || !dateRange?.to) return 0
    return Math.ceil((dateRange.to.getTime() - dateRange.from.getTime()) / (1000 * 60 * 60 * 24))
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            "justify-start text-left font-normal min-w-[200px]",
            !dateRange && "text-muted-foreground",
            className
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {dateRange?.from ? (
            dateRange.to ? (
              <>
                {format(dateRange.from, "MMM d, yyyy")} - {format(dateRange.to, "MMM d, yyyy")}
              </>
            ) : (
              format(dateRange.from, "MMM d, yyyy")
            )
          ) : (
            <span>Pick a date range</span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="end">
        <div className="flex">
          {/* Presets sidebar */}
          <div className="border-r border-black/[0.06] dark:border-white/[0.06] p-2 space-y-1 bg-black/[0.02] dark:bg-white/[0.02]">
            <p className="text-xs font-medium text-muted-foreground px-2 py-1">Quick select</p>
            {presets.map((preset) => {
              // Highlight if duration matches (simple heuristic)
              // Ideally we'd check exact dates for custom ranges, but calculating 'getValue' on render might be overkill
              const isSelected = preset.days ? getDaysSelected() === preset.days : false
              
              return (
                <Button
                  key={preset.label}
                  variant={isSelected ? "secondary" : "ghost"}
                  size="sm"
                  className="w-full justify-start text-xs h-7"
                  onClick={() => handlePresetClick(preset)}
                >
                  {preset.label}
                </Button>
              )
            })}
          </div>
          {/* Calendar */}
          <div className="p-2">
            <Calendar
              initialFocus
              mode="range"
              defaultMonth={dateRange?.from}
              selected={dateRange}
              onSelect={onDateRangeChange}
              numberOfMonths={2}
              disabled={{ after: new Date() }}
            />
          </div>
        </div>
        {/* Footer */}
        <div className="border-t border-black/[0.06] dark:border-white/[0.06] p-2 flex justify-between items-center bg-black/[0.02] dark:bg-white/[0.02]">
          <p className="text-xs text-muted-foreground px-2">
            {getDaysSelected() > 0 && `${getDaysSelected()} days selected`}
          </p>
          <div className="flex gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onDateRangeChange(undefined)}
            >
              Clear
            </Button>
            <Button
              size="sm"
              onClick={() => setOpen(false)}
            >
              Apply
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}
